// A single, page-wide IntersectionObserver shared by every ProductCard.
// One instance watches many card elements; each card fires its in-view
// callback once, then is unobserved.

let observer: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, () => void>();

// Treat "effectively fully visible" as in-view. Exact 1.0 can never be
// reached due to sub-pixel rounding, so wake up just below full visibility.
const DEFAULT_IN_VIEW_RATIO = 0.99;

function getObserver(inViewRatio: number): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;

  if (observer === null) {
    const handleEntries = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.intersectionRatio < inViewRatio) continue;
        callbacks.get(entry.target)?.();
        observer?.unobserve(entry.target);
        callbacks.delete(entry.target);
      }
    };

    observer = new IntersectionObserver(handleEntries, {
      threshold: [inViewRatio, 1],
    });
  }
  return observer;
}

/**
 * Observe an element and invoke `onInView` the first time it becomes ~100%
 * visible. Returns a cleanup function that stops observing the element.
 */
export function observeCard(
  element: Element,
  onInView: () => void,
  customInViewRatio?: number | undefined,
): () => void {
  const inViewRatio = customInViewRatio || DEFAULT_IN_VIEW_RATIO;
  const obs = getObserver(inViewRatio);
  if (!obs) return () => {};
  callbacks.set(element, onInView);
  obs.observe(element);
  return () => {
    obs.unobserve(element);
    callbacks.delete(element);
  };
}
